import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ShieldCheck, Building2, KeyRound, Smartphone } from 'lucide-react';
import { Card, Button } from '../components';
import { useAuthStore } from '../store/authStore';
import { checkToegangscode, checkUitnodiging, registreerMedewerker } from '../services/medewerkerApi';

const CODE_REGEX = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/;

/** Normaliseert invoer naar XXXX-XXXX (hoofdletters, koppelteken na 4 tekens). */
function formatCode(raw: string): string {
  const schoon = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  return schoon.length > 4 ? `${schoon.slice(0, 4)}-${schoon.slice(4)}` : schoon;
}

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

interface Bedrijf {
  bedrijfsnaam: string | null;
  omschrijving: string | null;
}

/**
 * Registratie voor het anonieme Medewerkersportaal.
 * Stap 1: toegangscode van de werkgever. Stap 2: e-mail + wachtwoord (+ optioneel mobiel).
 */
export const MedewerkerRegistratiePage: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAuthStore((s) => s.login);

  const [stap, setStap] = useState<1 | 2>(1);
  const [code, setCode] = useState('');
  const [uitnodigingToken, setUitnodigingToken] = useState<string | null>(null);
  const [voornaam, setVoornaam] = useState<string | null>(null);
  const [bedrijf, setBedrijf] = useState<Bedrijf | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [formFout, setFormFout] = useState<string | null>(null);

  const checkMutation = useMutation({
    mutationFn: (c: string) => checkToegangscode(c),
    onSuccess: (data) => setBedrijf(data),
    onError: () => setBedrijf(null),
  });

  const checkUitnodigingMutation = useMutation({
    mutationFn: (tok: string) => checkUitnodiging(tok),
    onSuccess: (data) => {
      setVoornaam(data.voornaam);
      setBedrijf({ bedrijfsnaam: data.bedrijfsnaam, omschrijving: null });
    },
  });

  const registreerMutation = useMutation({
    mutationFn: () =>
      registreerMedewerker({
        ...(uitnodigingToken ? { uitnodigingToken } : { code }),
        email: email.trim(),
        password,
        phone: phone.trim() ? phone.trim() : undefined,
      }),
    onSuccess: (data) => {
      login(data.user, data.token);
      navigate('/medewerker');
    },
  });

  // Prefill via ?token=... (proactieve activatielink uit de bulk-uitnodiging).
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setUitnodigingToken(token);
      checkUitnodigingMutation.mutate(token, { onSuccess: () => setStap(2) });
      return;
    }
    // Prefill via ?code=XXXX-XXXX (bijv. uit een QR-code of link van de werkgever).
    const prefill = searchParams.get('code');
    if (!prefill) return;
    const geformatteerd = formatCode(prefill);
    setCode(geformatteerd);
    if (CODE_REGEX.test(geformatteerd)) {
      checkMutation.mutate(geformatteerd, { onSuccess: () => setStap(2) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const codeGeldig = CODE_REGEX.test(code);

  const handleCodeChange = (waarde: string) => {
    setCode(formatCode(waarde));
    setBedrijf(null);
    checkMutation.reset();
  };

  const handleCodeBlur = () => {
    if (codeGeldig && !bedrijf && !checkMutation.isPending) checkMutation.mutate(code);
  };

  const handleStap1 = (e: React.FormEvent) => {
    e.preventDefault();
    if (!codeGeldig) return;
    if (bedrijf) {
      setStap(2);
      return;
    }
    checkMutation.mutate(code, { onSuccess: () => setStap(2) });
  };

  const handleStap2 = (e: React.FormEvent) => {
    e.preventDefault();
    setFormFout(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setFormFout(t('registratie.foutEmail', { defaultValue: 'Vul een geldig e-mailadres in.' }));
      return;
    }
    if (password.length < 8) {
      setFormFout(t('registratie.foutWachtwoord', { defaultValue: 'Je wachtwoord moet minimaal 8 tekens lang zijn.' }));
      return;
    }
    registreerMutation.mutate();
  };

  const inputClass =
    'w-full rounded-lg border border-gray-300 px-3 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] focus:border-[#0B1F3A]';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-md mx-auto px-4 py-8 space-y-5">
        <div className="text-center">
          <span className="text-2xl font-bold text-[#0B1F3A] tracking-tight">BeLagom</span>
          <h1 className="text-xl font-semibold text-gray-900 mt-3">
            {t('registratie.titel', { defaultValue: 'Registreren als medewerker' })}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {t('registratie.subtitel', { defaultValue: 'Anoniem toegang tot professionals, Academy en de Bedrijfsthermometer.' })}
          </p>
        </div>

        {stap === 1 && (
          <Card className="p-5">
            <form onSubmit={handleStap1} className="space-y-4" noValidate>
              <div>
                <label htmlFor="toegangscode" className="block text-sm font-medium text-gray-800 mb-1 flex items-center gap-1.5">
                  <KeyRound className="w-4 h-4 text-gray-500" aria-hidden="true" />
                  {t('registratie.codeLabel', { defaultValue: 'Toegangscode van je werkgever' })}
                </label>
                <input
                  id="toegangscode"
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="XXXX-XXXX"
                  value={code}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  onBlur={handleCodeBlur}
                  className={`${inputClass} font-mono tracking-widest uppercase text-center text-lg`}
                  maxLength={9}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('registratie.codeHulp', { defaultValue: 'Je vindt deze code op de kaart, poster of e-mail van je werkgever.' })}
                </p>
              </div>

              {checkMutation.isPending && (
                <p className="text-sm text-gray-500">{t('registratie.codeControleren', { defaultValue: 'Code controleren…' })}</p>
              )}

              {checkMutation.isError && (
                <p className="text-sm text-red-600" role="alert">
                  {foutMelding(checkMutation.error, t('registratie.codeOngeldig', { defaultValue: 'Deze toegangscode is ongeldig of verlopen.' }))}
                </p>
              )}

              {bedrijf && (
                <div className="rounded-lg bg-green-50 border border-green-100 p-3 flex items-start gap-2">
                  <Building2 className="w-5 h-5 text-green-700 shrink-0" aria-hidden="true" />
                  <div className="text-sm">
                    <p className="text-gray-900 font-medium">
                      {t('registratie.bijBedrijf', {
                        defaultValue: 'Je registreert je bij {{bedrijf}}',
                        bedrijf: bedrijf.bedrijfsnaam ?? t('registratie.onbekendBedrijf', { defaultValue: 'je werkgever' }),
                      })}
                    </p>
                    {bedrijf.omschrijving && <p className="text-gray-600 mt-0.5">{bedrijf.omschrijving}</p>}
                  </div>
                </div>
              )}

              <Button type="submit" size="lg" className="w-full" disabled={!codeGeldig || checkMutation.isPending} loading={checkMutation.isPending}>
                {t('registratie.verder', { defaultValue: 'Verder' })}
              </Button>
            </form>
          </Card>
        )}

        {stap === 2 && (
          <Card className="p-5">
            <div className="rounded-lg bg-green-50 border border-green-100 p-3 flex items-start gap-2 mb-4">
              <Building2 className="w-5 h-5 text-green-700 shrink-0" aria-hidden="true" />
              <div className="text-sm flex-1">
                <p className="text-gray-900 font-medium">
                  {voornaam
                    ? t('registratie.bijBedrijfMetNaam', {
                        defaultValue: 'Welkom {{voornaam}} — je registreert je bij {{bedrijf}}',
                        voornaam,
                        bedrijf: bedrijf?.bedrijfsnaam ?? t('registratie.onbekendBedrijf', { defaultValue: 'je werkgever' }),
                      })
                    : t('registratie.bijBedrijf', {
                        defaultValue: 'Je registreert je bij {{bedrijf}}',
                        bedrijf: bedrijf?.bedrijfsnaam ?? t('registratie.onbekendBedrijf', { defaultValue: 'je werkgever' }),
                      })}
                </p>
                {!uitnodigingToken && (
                  <button type="button" onClick={() => setStap(1)} className="text-xs text-[#0B1F3A] underline mt-1">
                    {t('registratie.andereCode', { defaultValue: 'Andere code gebruiken' })}
                  </button>
                )}
              </div>
            </div>

            {checkUitnodigingMutation.isError && (
              <p className="text-sm text-red-600 mb-4" role="alert">
                {foutMelding(checkUitnodigingMutation.error, t('registratie.uitnodigingOngeldig', { defaultValue: 'Deze activatielink is ongeldig of al gebruikt.' }))}
              </p>
            )}

            <form onSubmit={handleStap2} className="space-y-4" noValidate>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-800 mb-1">
                  {t('registratie.email', { defaultValue: 'E-mailadres' })}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('registratie.emailHulp', { defaultValue: 'Alleen om in te loggen. Je werkgever ziet dit nooit.' })}
                </p>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-800 mb-1">
                  {t('registratie.wachtwoord', { defaultValue: 'Wachtwoord' })}
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('registratie.wachtwoordHulp', { defaultValue: 'Minimaal 8 tekens.' })}
                </p>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-800 mb-1 flex items-center gap-1.5">
                  <Smartphone className="w-4 h-4 text-gray-500" aria-hidden="true" />
                  {t('registratie.mobiel', { defaultValue: 'Mobiel nummer (optioneel)' })}
                </label>
                <input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="06 12345678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={inputClass}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('registratie.mobielHulp', {
                    defaultValue: 'Alleen nodig voor het SMS-akkoord op Poortwachter-documenten (bijv. een plan van aanpak). Je kunt dit ook later invullen.',
                  })}
                </p>
              </div>

              <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 flex items-start gap-2 text-sm">
                <ShieldCheck className="w-5 h-5 text-[#0B1F3A] shrink-0" aria-hidden="true" />
                <div className="text-gray-700">
                  <p className="font-medium text-gray-900">{t('registratie.privacyTitel', { defaultValue: 'Zo blijf je anoniem' })}</p>
                  <p className="mt-0.5">
                    {t('registratie.privacyUitleg', {
                      defaultValue:
                        'Je krijgt een pseudoniem zoals MDW-00012-A. Je werkgever ziet alleen dat pseudoniem — nooit je naam, e-mail of nummer. Alleen jij en de professional die je kiest weten wie je bent.',
                    })}
                  </p>
                </div>
              </div>

              {(formFout || registreerMutation.isError) && (
                <p className="text-sm text-red-600" role="alert">
                  {formFout ??
                    foutMelding(registreerMutation.error, t('registratie.mislukt', { defaultValue: 'Registreren is niet gelukt. Probeer het opnieuw.' }))}
                </p>
              )}

              <Button type="submit" size="lg" className="w-full" loading={registreerMutation.isPending}>
                {t('registratie.account', { defaultValue: 'Account aanmaken' })}
              </Button>
            </form>
          </Card>
        )}

        <p className="text-center text-sm text-gray-600">
          {t('registratie.alAccount', { defaultValue: 'Heb je al een account?' })}{' '}
          <Link to="/login" className="text-[#0B1F3A] font-medium underline">
            {t('registratie.inloggen', { defaultValue: 'Inloggen' })}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default MedewerkerRegistratiePage;
